import { inject, Injectable } from '@angular/core';
import { runtimeEventManager, stringStartsWith } from '@wm/core';
import { byType, extractType, getMetaDataFromData, isPageable } from '@wms/core';
import {
  concat,
  extend,
  filter,
  forEach,
  get, groupBy,
  includes,
  isArray,
  isEmpty,
  isObject,
  keys,
  map,
  orderBy,
  replace,
  toLower
} from 'lodash-es';
import { ProjectService } from '../project/project.service';
import { WebserviceService } from '../webservices/webservice.service';
import { getVariableByName } from '@wms/variables';
import { ServiceFactoryService } from "@wms/services";

//TODO: Integrate with API
const baseDataTypes = {
  "java.lang.Float": {
    "primitiveType": "NUMBER", "internal": false
  }, "java.sql.Time": {
    "primitiveType": "DATE", "internal": false
  }, "java.lang.Double": {
    "primitiveType": "NUMBER", "internal": false
  }, "java.util.Date": {
    "primitiveType": "DATE", "internal": false
  }, "java.time.LocalDateTime": {
    "primitiveType": "DATETIME", "internal": true
  }, "float": {
    "primitiveType": "NUMBER", "internal": true
  }, "java.lang.Integer": {
    "primitiveType": "NUMBER", "internal": false
  }, "long": {
    "primitiveType": "NUMBER", "internal": true
  }, "java.math.BigDecimal": {
    "primitiveType": "NUMBER", "internal": false
  }, "java.lang.Character": {
    "primitiveType": "STRING", "internal": false
  }, "java.lang.Long": {
    "primitiveType": "NUMBER", "internal": false
  }, "java.lang.Short": {
    "primitiveType": "NUMBER", "internal": false
  }, "java.lang.StringBuffer": {
    "primitiveType": "STRING", "internal": false
  }, "java.lang.Boolean": {
    "primitiveType": "BOOLEAN", "internal": false
  }, "java.math.BigInteger": {
    "primitiveType": "NUMBER", "internal": false
  }, "java.lang.String": {
    "primitiveType": "STRING", "internal": false
  }, "java.lang.Byte": {
    "primitiveType": "NUMBER", "internal": false
  }, "byte": {
    "primitiveType": "NUMBER", "internal": true
  }, "double": {
    "primitiveType": "NUMBER", "internal": true
  }, "java.sql.Timestamp": {
    "primitiveType": "DATE", "internal": false
  }, "int": {
    "primitiveType": "NUMBER", "internal": true
  }, "java.sql.Date": {
    "primitiveType": "DATE", "internal": false
  }, "boolean": {
    "primitiveType": "BOOLEAN", "internal": true
  }, "char": {
    "primitiveType": "STRING", "internal": true
  }, "short": {
    "primitiveType": "NUMBER", "internal": true
  }
};

@Injectable({
  providedIn: 'root'
})
export class TypeFactoryService {
  dataTypes: any;
  services: any;
  allServices: any = [];
  requestQueue: any;
  projectService: any;
  webService: any;
  serviceFactory: ServiceFactoryService;
  dataTypesCache = new Map<string, any>([['baseDataTypes', baseDataTypes]]);
  loadAllDataTypes:boolean = false;

  constructor() {
    this.projectService = inject(ProjectService);
    this.webService = inject(WebserviceService);
    this.serviceFactory = inject(ServiceFactoryService);
    runtimeEventManager.setService('TypeFactoryService', this);
    this.setDataTypesFromCache()
  }

  /*TODO: Removied  getDatatypes  methods and using  getDataTypesByService*/

  /* Fetch the available dataTypes from backend (required for web service return types)*/
  // getDataTypes(reloadFlag ?: boolean, showSpinner?: boolean) {
  //
  //   if(this.dataTypes && !reloadFlag) {
  //     return Promise.resolve(this.dataTypes);
  //   }
  //   /*data for the request*/
  //   let urlParams = {
  //     projectID: this.projectService.getProjectId()
  //   };
  //
  //   return (this.webService.listTypes(urlParams, showSpinner) as Promise<any>).then((response) => {
  //     this.dataTypes = response.types;
  //     Promise.resolve(this.dataTypes);
  //     /*TODO: to be removed from rootScope and put in ServiceFactory*/
  //     // $rootScope.dataTypes = response.types;
  //     // Utils.triggerFn(successCallback);
  //   }).catch((err) => {
  //     Promise.reject(err);
  //     // TO DO
  //     //Utils.triggerFn(errorCallback);
  //   });
  // }

  getAllDataTypes(showSpinner: boolean, reloadForModelVariable:boolean=false) {
    if (this.dataTypes && this.loadAllDataTypes && !reloadForModelVariable) {
      return Promise.resolve(this.dataTypes);
    }
    /*data for the request*/
    let urlParams = {
      projectID: this.projectService.getProjectId()
    };

    return (this.webService.listTypes(urlParams, showSpinner) as Promise<any>).then((response) => {
      this.loadAllDataTypes = true;
      let allDataTypes = groupBy(response.types, 'service');
      delete allDataTypes['undefined']
      if(reloadForModelVariable){
        this.updateDataTypesCache('MyJavaService')
      }
      this.deduplicateAndConvertWithForEach(allDataTypes, response.types);
      Promise.resolve(this.getTypes());
    }).catch((err) => {
      Promise.reject(err);
      // TO DO
      //Utils.triggerFn(errorCallback);
    });
  }

  deduplicateAndConvertWithForEach(input: any, res: any) {
    const result: any = {};
    // Iterate over each key in the input object
    Object.keys(input).forEach(service => {
      // Create an empty object to hold the unique objects for the current key
      const uniqueObjects: any = {};
      Object.keys(res).forEach(key => {
        if (res[key].service === service) {
          uniqueObjects[key] = res[key];
        }
      })
      result[service] = uniqueObjects;
      if (!this.dataTypesCache.has(service)) {
        this.dataTypesCache.set(service, result[service]);
        this.setDataTypesFromCache();
      }
    });

  }

  getDataTypesByService(service: any, showSpinner: boolean, reloadFlag?:boolean) {
    if (this.dataTypesCache.has(service) && !reloadFlag) {
      return Promise.resolve(this.getTypes());
    }
    return (this.webService.getDataTypesByService(service, showSpinner) as Promise<any>).then((response) => {
      this.dataTypesCache.set(service, response.types);
      this.setDataTypesFromCache();
      Promise.resolve(this.getTypes());
    }).catch((err) => {
      Promise.reject(err);
    });
  }

  //getter method for fetched dataTypes
  getTypes() {
    return this.dataTypes;
  }

  updateDataTypesCache(service: string) {
    this.dataTypesCache.delete(service);
    this.setDataTypesFromCache();
  }

  setDataTypesFromCache() {
    let dataTypes = {};
    for(let value of this.dataTypesCache.values()) {
      dataTypes = Object.assign(dataTypes, value)
    }
    this.dataTypes = dataTypes;
  }

  getPath = (expr: string, regEx: any) => {
    let path = expr.replace(regEx, '');
    if (includes(path, '$i')) {
      path = replace(path, '$i', '0');
    }
    return path;
  }

  /**
 * returns a list of objects having fields, against an expression
 * for a static variable the fields are evaluated against the dataSet
 * for other types. like LiveVariable, ServiceVariable, or Widgets bound to them,
 * the type is evaluated against the expression and fields are evaluated against the same
 *
 * TODO: handle case where the expression is a widget expression and widget is bound to a StaticVariable
 * @param expr
 * @param options
 * @returns {*}
 */
  getFieldsForExpr = (expr: string, options?: any) => {
    if (!expr) {
      return [];
    }
    const isPrimitiveType = (type: any) => {
      let types = this.dataTypes,
        typeService = get(types, '[' + type + '].service');
      return typeService ? false : true;
    }

    const returnFields = (fieldDefs: any) => {
      // filtering fields depending on relative or terminal nodes and order them by field name.
      let relativeFields: any = [], terminalFields: any = [];
      forEach(fieldDefs, (fieldDef) => {
        if (includes(fieldDef.field, '.')) {
          relativeFields.push(fieldDef);
        } else {
          terminalFields.push(fieldDef);
        }
      });
      fieldDefs = concat(orderBy(terminalFields, 'field'), orderBy(relativeFields, 'field'));

      if (options.getFieldInfo) {
        return options.filter === 'terminal' ? filter(fieldDefs, { 'type': 'terminal' }) : fieldDefs;
      }
      //Live Widgets need only array of field names
      return map(fieldDefs, 'field');
    }

    if (stringStartsWith(expr, 'Variables.')){
      expr = 'bind:'+expr;
    }
    let isBoundToVariable = stringStartsWith(expr, 'bind:Variables.') ,
      isBoundToWidget = stringStartsWith(expr, 'bind:Widgets.'),
      parts,
      variable,
      path,
      type,
      widgetScope;

    options = options || {};
    if (isBoundToVariable) {
      parts = expr.split(/\W/);
      variable = getVariableByName(parts[2]) || {};
      if ((variable.category === 'wm.Variable' && isPrimitiveType(variable.type)) || variable.category === 'wm.DeviceVariable') {
        expr = expr.replace('bind:Variables.' + variable.name + '.', '');
        expr = expr.replace(/\$i/g, '0');
        return returnFields(this.getFieldObjectsForData(get(variable, expr)));
      }
    }
    type = this.getTypeForExpression(expr);
    // Incase of static variable
    if (isBoundToWidget && !get(this.dataTypes, type)) {
      parts = expr.split(/\W/);
      widgetScope = byType('form-widgets')[parts[2]];
      let currentItemRegEx = /^bind:Widgets\..*\.currentItem([\.])/g;
      let selectedItemRegEx = /^bind:Widgets\..*\.selecteditem([\.])/g;
      if (currentItemRegEx.test(expr)) {
        path = this.getPath(expr, currentItemRegEx);
      }

      if (selectedItemRegEx.test(expr)) {
        path = this.getPath(expr, selectedItemRegEx);
      }

      /*
       * when a prefab outbound property is bound to a widget, the fields should be extracted from the prefab
       * property's scope, For example if parts is  ["bind", "Widgets", "TestPrefab1", "out3", "results"],
       * then the dataset should be extracted from widgetScope[out3.results]
      */
      let dataset = get(widgetScope, 'prefabname') ? get(widgetScope, parts.slice(3, parts.length).join('.')) : get(widgetScope, 'dataset');
      let data = path ? isArray(dataset) ? get(dataset[0], path) : get(dataset, path) : dataset;
      return returnFields(this.getFieldObjectsForData(data));
    }
    return returnFields(this.getFieldObjectsForType(type, options));
  }

  /**
   * Iterates though the dataSet passed and returns an array of objects having fields in the dataSet
   * @param data
   * @param options
   * @returns {Array}
   */
  getFieldObjectsForData = (data: any, options?: any) => {
    options = options || {};

    let defs: any = [],
      prefix = options.prefix || '',
      subDefs,
      dataObject;

    dataObject = getMetaDataFromData(data);

    // if data is not an object, then do not generate the options.
    if (!isObject(dataObject)) {
      return defs;
    }

    forEach(dataObject, (val, key) => {
      if (isObject(val) && !isEmpty(val)) {
        defs.push({
          'field': prefix + key,
          'type': 'object'
        });
        if (Array.isArray(val)) {
          options.prefix = prefix + key + '[0].';
          subDefs = this.getFieldObjectsForData(val[0], options);
        } else {
          options.prefix = prefix + key + '.';
          subDefs = this.getFieldObjectsForData(val, options);
        }
        defs = defs.concat(subDefs);
      } else {
        defs.push({
          'field': prefix + key,
          'type': 'terminal'
        });
      }
    });

    return defs;
  }

  /**
   * Returns an array of fields against passed data-type
   * traverses the node in $rs.dataTypes and collects the fields & sub-fields(recursively)
   * @param type data-type
   * @param options config options like
   *  - filter, decides the type of fields to return, it can be:
   *      - object, return only object type nodes
   *      - terminal(default), return only primitive type fields and sub-fields
   *      - all, return all types
   * @returns {Array|*} array of fields
   */
  getFieldsForType = (type: string, options?: any) => {
    options = options || {};
    let fieldObjects = this.getFieldObjectsForType(type);

    switch (options.filter) {
      case 'object':
        fieldObjects = filter(fieldObjects, { 'type': 'object' });
        break;
      case 'all':
        break;
      default:
        fieldObjects = filter(fieldObjects, { 'type': 'terminal' });
        break;
    }

    return options.getFieldInfo ? fieldObjects : map(fieldObjects, 'field');
  }

  /**
   * Returns an array of field objects against a data-type
   * Looks into $rs.dataTypes for the fields and sub-fields(recursively) against the passed data-type.
   * @param type data-type
   * @param options config options, used internally by the function, not to be passed from caller.
   *  - field 'visitedTypes' in options is used to track data types already processed. Used only for db type services
   * @param typeChain used to track processed types, used only for web service types
   * @returns {Array} of objects
   */
  getFieldObjectsForType = (type: any, options?: any, typeChain?: any) => {
    options = options || {};
    let fields: any[] = [],
      types = this.dataTypes,
      typeDef = get(types, '["' + type + '"]') || {},
      fieldDefs = typeDef.fields,
      service = typeDef.service,
      isDataService = false,
      prefix = options.prefix || '',
      tempSubFields = [],
      subFields: any = [],
      typeChainArr;

    //For reset service which returns pageable object, get fields from content object inside the dataset
    if (isPageable(fieldDefs)) {
      typeDef = get(types, '["' + fieldDefs.content.type + '"]') || {};
      fieldDefs = typeDef.fields;
      service = typeDef.service;
    }

    if (service && this.serviceFactory.getServiceObjectByName(service).type === 'DataService') {
        isDataService = true;
    }

    // for db services, further drill down not allowed for list type nodes, to avoid recursive relation loops
    if (isDataService) {
      if (!options.visitedTypes) {
        options.visitedTypes = [];
      } else {
        if (includes(options.visitedTypes, type)) {
          return fields;
        }
        options.visitedTypes.push(type);
      }
    } else {
      typeChain = typeChain || '';
      typeChainArr = typeChain.split('~wm~');
      if (includes(typeChainArr, type)) {
        return keys(fieldDefs);
      }
      typeChainArr.push(type);
      typeChain = typeChainArr.join('~wm~');

      /*
        [WMS-22254] TODO: temp condition to halt fetching of n levels of fields, causing the studio to freeze.
        Studio experience to select a particular field should change to support nested structure till n-levels.
        e.g something like a tree structure, where next level is loaded on-demand.
       */
      let MAX_METADATA_TREE_LEVEL = 5
      if (typeChainArr.length === MAX_METADATA_TREE_LEVEL) {
        return [];
      }
    }

    forEach(fieldDefs, (fieldDef, field) => {
      //todo: Added "." condition, temporary fix to get fields for procedure which returns cursor
      if (!isDataService ||
        (!fieldDef.isList || !get(types, '["' + fieldDef.type + '"].fields')) ||
        !includes(type, '.')) {
        options.prefix = prefix + (fieldDef.isList ? field + '[0]' : field) + '.';
        tempSubFields = this.getFieldObjectsForType(fieldDef.type, options, typeChain);
        subFields = subFields.concat(tempSubFields);
        if (tempSubFields.length || includes(options.visitedTypes, fieldDef.type)) {
          fields.push({
            'field': prefix + field,
            'type': 'object'
          });
        } else {
          fields.push({
            'field': prefix + field,
            'type': 'terminal',
            'dataType': toLower(extractType(fieldDef.type))
          });
        }
      } else if (fieldDef.isList) {
        // for list type db fields(many-to-one, like employees for a department in hrdb), push it as object node
        fields.push({
          'field': prefix + field,
          'type': 'object'
        });
      }
    });

    /* append subFields with current fields in the end */
    fields = fields.concat(subFields);
    return fields;
  }

  /**
 * Returns the type for an expression having bind:Widgets.xyz, i.e. bound to a Widget property
 * The widget(in the expression) in turn can be bound to an expression, so calls the 'getTypeForExpression' routine to evaluate that expr
 * @param expr
 * @returns {*}
 */
  getTypeFromWidgetExpr = (expr: string) => {
    let parts = expr.split('.'),
      widgetScope = byType('form-widgets')[parts[1]],
      widgetProp = parts[2],
      type,
      datafield = get(widgetScope, 'datafield');

    /* sanity check */
    if (!widgetScope) {
      return;
    }

    /*
     * type of widget property(found in expression) will be dependent on another property
     * this will be found through getTypeFrom
     * if not found, assume it to be 'dataset'
     */
    widgetProp = get(widgetScope, 'widgetProps[' + widgetProp + '].getTypeFrom') || 'dataset';

    if (stringStartsWith(widgetProp, 'expr:')) {
      type = widgetScope.$eval(widgetProp.replace('expr:', ''));
    } else {
      type = this.getTypeForExpression(widgetScope['bind' + widgetProp], widgetScope);
    }

    type = this.extractFinalTypeFromExpr(type, expr);

    if (datafield && datafield !== 'All Fields') {
      type = this.getChildNodeType(type, datafield);
    }

    return type;
  }

  /**
   * Evaluates and returns the data-type for an expression
   * If expr is a Variable, gets the type from variable's source service
   * If expr is a Widget, finds the source Variable, gets the type from it.
   * e.g.
   * - for LiveVar Variables.HrdbUserData.dataSet, packageType (com.hrdb.User) will be returned.
   * - for ServiceVar Variables.GoogleapisInvoke.dataSet.route.leg.step, type against 'step' in rootType node will be returned
   * - Widgets.grid1.selecteditem, the type of expression bound to grid's dataset will be returned
   * - Widgets.grid1.selecteditem.department, type against 'department' node in the type of expression bound to grid's dataset will be returned
   * @param expr
   * @param widgetScope
   * @param dataField
   * @param scope
   * @returns {*}
   */
  getTypeForExpression = (expr: string, widgetScope?: any, dataField?: string, scope?: any) => {
    /* sanity check */
    if (!expr) {
      return;
    }
    expr = expr.replace(/\[\$i\]|\[0\]/g, '');
    let isBoundToVariable = includes(expr, 'bind:Variables.'),
      isBoundToWidget = includes(expr, 'bind:Widgets.'),
      type,
      datafield = get(widgetScope, dataField || 'datafield');

    if (isBoundToVariable) {
      //Pass page/partial's scope where variable is available
      type = this.getTypeFromVarExpr(expr, scope);
    } else if (isBoundToWidget) {
      type = this.getTypeFromWidgetExpr(expr);
    }

    if (datafield && datafield !== 'All Fields') {
      type = this.getChildNodeType(type, datafield);
    }

    return type;
  }

  /**
   * Returns the type for an expression having bind:Variables.xyz, i.e. bound to a Variable
   * finds the type from variable object
   * based on further nodes in the expression, finds the resultant type
   * @param expr
   * @param scope page/partial where variable is available
   * @returns {*}
   */
  getTypeFromVarExpr = (expr: string, scope: any) => {
    let parts = expr.split('.'),
      // variable = { type: '', category: '', package: '' }, type;
    variable = getVariableByName(parts[1], scope),
      type;

    if (!variable) {
      return;
    }

    if (variable.category === 'wm.ServiceVariable') {
      type = variable.type;
    } else if (variable.category === 'wm.LiveVariable') {
      type = variable.package;
      expr = expr.replace('dataSet.data', 'dataSet');
    } else {
      type = variable.type;
    }

    return this.extractFinalTypeFromExpr(type, expr);
  }

  /**
   * Extracts the final datatype for the last node in the expression
   * e.g.
   * - for Variables.HrdbUserData.dataSet, rootType will be returned
   * - for Variables.GoogleapisInvoke.dataSet.route.leg.step, type against 'step' in rootType node will be returned
   * - Widgets.grid1.selecteditem, rootType will be returned
   * - Widgets.grid1.selecteditem.department, type against 'department' in rootType will be returned
   * @param rootType
   * @param expr
   * @returns {*}
   */
  extractFinalTypeFromExpr = (rootType: any, expr: string) => {
    let parts = expr.split('.');
    parts.splice(0, 3);
    return this.getChildNodeType(rootType, parts.join('.'));
  }

  /**
   * Extracts the datatype for internal nodes(separated by '.') in a rootType
   * e.g. rootType for GoogleapisInvoke is googleapis.DirectionsResponse
   * expr can be route.leg.step, hence type for step node will be returned
   * @param rootType
   * @param expr
   * @returns {*}
   */
  getChildNodeType = (rootType: any, expr: string) => {
    expr = expr || '';
    let type = rootType,
      parts = expr.split('.');
    if (expr) {
      parts.forEach((part) => {
        type = get(this.dataTypes, '["' + type + '"].fields.' + part + '.type');
      });
    }
    return type;
  }
}
